/**
 * Saisie des dates au format français (jj/mm/aaaa, hh:mm, mm/aaaa), quelle que
 * soit la langue du navigateur. Les champs natifs `date`, `time`, `month` et
 * `datetime-local` s'affichent dans la langue du navigateur (mm/dd/yyyy, AM/PM) :
 * les composants de `components/ui/date-input.tsx` les remplacent par un texte
 * masqué et gardent la même valeur technique (`YYYY-MM-DD`, `HH:mm`, `YYYY-MM`,
 * `YYYY-MM-DDTHH:mm`), donc les formulaires et l'API ne changent pas.
 *
 * Fonctions pures : `mask*` met en forme la frappe, `parse*` renvoie la valeur
 * technique ou `null` si le texte n'est pas (encore) une date valide, `format*`
 * fait l'inverse.
 */

/** Libellés français des mois, pour le calendrier et le sélecteur de mois. */
export const FRENCH_MONTHS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
] as const;

export const FRENCH_MONTHS_SHORT = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
] as const;

/** Jours de la semaine, lundi en premier (usage français). */
export const FRENCH_WEEKDAYS_SHORT = ['lu', 'ma', 'me', 'je', 've', 'sa', 'di'] as const;
export const FRENCH_WEEKDAYS = [
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
  'dimanche',
] as const;

const pad2 = (n: number): string => String(n).padStart(2, '0');

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Année saisie sur 2 chiffres → 20xx ; 4 chiffres gardés tels quels. */
function fullYear(text: string): number | null {
  if (/^\d{4}$/.test(text)) {
    const year = Number(text);
    return year >= 1900 && year <= 2999 ? year : null;
  }
  if (/^\d{2}$/.test(text)) {
    return 2000 + Number(text);
  }
  return null;
}

/**
 * Met en forme une frappe en groupes de chiffres (`groups`) séparés par `seps`
 * (`seps[i]` suit le groupe `i`). Un groupe plein déborde sur le suivant, donc
 * « 05112026 » tapé d'affilée donne « 05/11/2026 » ; un séparateur tapé à la
 * main ferme le groupe en cours (« 5/1/26 » reste tel quel). Les chiffres en
 * trop sont ignorés. `raw` a déjà ses séparateurs ramenés à `/`, `:` ou espace.
 */
function maskGroups(raw: string, groups: readonly number[], seps: readonly string[]): string {
  const segments = raw.replace(/[^\d/: ]/g, '').split(/[/: ]/);
  const trailing = segments.length > 1 && segments[segments.length - 1] === '';
  const parts: string[] = [];
  for (const segment of segments) {
    let rest = segment;
    let size = groups[parts.length];
    while (rest !== '' && size !== undefined) {
      parts.push(rest.slice(0, size));
      rest = rest.slice(size);
      size = groups[parts.length];
    }
  }
  const text = parts
    .map((part, index) => (index === 0 ? part : `${seps[index - 1] ?? ''}${part}`))
    .join('');
  // Un séparateur tapé en fin de texte est gardé, tant qu'un groupe reste à remplir.
  const next = seps[parts.length - 1];
  return trailing && next !== undefined && parts.length < groups.length ? `${text}${next}` : text;
}

// --- Date jj/mm/aaaa --------------------------------------------------------

export function maskDateText(raw: string): string {
  return maskGroups(raw.replace(/[.\-\s:]/g, '/'), [2, 2, 4], ['/', '/']);
}

/** « 5/11/2026 », « 05/11/26 » → « 2026-11-05 » ; `null` si invalide. */
export function parseDateText(text: string): string | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(text.trim());
  if (match === null) {
    return null;
  }
  const [, dayText = '', monthText = '', yearText = ''] = match;
  const year = fullYear(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  if (year === null || month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    return null;
  }
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/** « 2026-11-05 » → « 05/11/2026 » ; chaîne vide si la valeur n'est pas une date. */
export function formatDateText(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match === null ? '' : `${match[3] ?? ''}/${match[2] ?? ''}/${match[1] ?? ''}`;
}

// --- Heure hh:mm --------------------------------------------------------------

export function maskTimeText(raw: string): string {
  return maskGroups(raw.replace(/[hH.\s/]/g, ':'), [2, 2], [':']);
}

/** « 9:05 », « 14:30 », « 14 » → « 09:05 », « 14:30 », « 14:00 » ; `null` si invalide. */
export function parseTimeText(text: string): string | null {
  const match = /^(\d{1,2})(?::(\d{2})?)?$/.exec(text.trim());
  if (match === null) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2] ?? '0');
  if (hours > 23 || minutes > 59) {
    return null;
  }
  return `${pad2(hours)}:${pad2(minutes)}`;
}

export function formatTimeText(value: string): string {
  return /^\d{2}:\d{2}$/.test(value) ? value : '';
}

// --- Mois mm/aaaa -------------------------------------------------------------

export function maskMonthText(raw: string): string {
  return maskGroups(raw.replace(/[.\-\s:]/g, '/'), [2, 4], ['/']);
}

/** « 3/2027 », « 03/27 » → « 2027-03 » ; `null` si invalide. */
export function parseMonthText(text: string): string | null {
  const match = /^(\d{1,2})\/(\d{2}|\d{4})$/.exec(text.trim());
  if (match === null) {
    return null;
  }
  const month = Number(match[1]);
  const year = fullYear(match[2] ?? '');
  if (year === null || month < 1 || month > 12) {
    return null;
  }
  return `${year}-${pad2(month)}`;
}

export function formatMonthText(value: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  return match === null ? '' : `${match[2] ?? ''}/${match[1] ?? ''}`;
}

// --- Date et heure jj/mm/aaaa hh:mm --------------------------------------------

export function maskDateTimeText(raw: string): string {
  const normalized = raw.replace(/[.-]/g, '/').replace(/[hH]/g, ':').replace(/\s+/g, ' ');
  return maskGroups(normalized, [2, 2, 4, 2, 2], ['/', '/', ' ', ':']);
}

/**
 * « 05/11/2026 14:30 » → « 2026-11-05T14:30 » (valeur d'un `datetime-local`).
 * Sans heure, la date seule vaut minuit.
 */
export function parseDateTimeText(text: string): string | null {
  const [datePart = '', timePart, ...rest] = text.trim().split(/\s+/);
  if (rest.length > 0) {
    return null;
  }
  const date = parseDateText(datePart);
  const time = timePart === undefined ? '00:00' : parseTimeText(timePart);
  return date === null || time === null ? null : `${date}T${time}`;
}

export function formatDateTimeText(value: string): string {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value);
  return match === null ? '' : `${formatDateText(match[1] ?? '')} ${match[2] ?? ''}`;
}

// --- Calendrier ---------------------------------------------------------------

/** Les 42 jours (6 semaines, lundi en premier) affichés pour le mois `YYYY-MM`. */
export function calendarDays(month: string): string[] {
  const [year = 1970, monthNumber = 1] = month.split('-').map(Number);
  const first = new Date(Date.UTC(year, monthNumber - 1, 1));
  const offset = (first.getUTCDay() + 6) % 7;
  const days: string[] = [];
  for (let i = 0; i < 42; i += 1) {
    days.push(new Date(Date.UTC(year, monthNumber - 1, 1 - offset + i)).toISOString().slice(0, 10));
  }
  return days;
}

/** Mois `YYYY-MM` décalé de `delta` mois. */
export function shiftMonth(month: string, delta: number): string {
  const [year = 1970, monthNumber = 1] = month.split('-').map(Number);
  return new Date(Date.UTC(year, monthNumber - 1 + delta, 1)).toISOString().slice(0, 7);
}

/** Date `YYYY-MM-DD` décalée de `delta` jours. */
export function shiftDay(date: string, delta: number): string {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + delta)).toISOString().slice(0, 10);
}

/** « novembre 2026 » pour `2026-11` ; la valeur brute si elle est mal formée. */
export function formatMonthLabel(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  const name = match === null ? undefined : FRENCH_MONTHS[Number(match[2]) - 1];
  return match === null || name === undefined ? month : `${name} ${match[1] ?? ''}`;
}

/** « jeudi 5 novembre 2026 », libellé accessible d'un jour du calendrier. */
export function formatDayLabel(date: string): string {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  const weekday = (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
  return `${FRENCH_WEEKDAYS[weekday] ?? ''} ${day} ${FRENCH_MONTHS[month - 1] ?? ''} ${year}`;
}

const PARIS_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Paris',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Aujourd'hui à Paris, `YYYY-MM-DD` (calendrier : jour mis en évidence). */
export function parisTodayDate(): string {
  return PARIS_DAY.format(new Date());
}
